import type { UVInsertConfig } from "../../models/uv-inserts"
import type { SuggestedMessaging } from "../../models/uv-inserts/suggested-messaging"
import type { LiquidProductVariant } from "../../models/shopify/liquid/variant"
import { getLiquidProductVariantImageSrc } from "./liquid-variant-image"
import { getFaceElementsForRegionTab, isUvInsertFrontRegionTab } from "./visualiser-tabs"

export type UvInsertChooserThemeItem = {
  /** Index in `config.themes` (stable selection key for consumers). */
  index: number
  variantId: string
  title: string
  imageSrc: string | null
}

export type UvInsertChooserSuggestionGroup = {
  key: string
  name: string
  suggestions: readonly string[]
}

/**
 * Theme grid items — caller should only use when {@link isUvInsertFrontRegionTab} and multiple themes.
 */
export function getUvInsertThemeChooserItems(options: {
  themes: LiquidProductVariant[]
}): UvInsertChooserThemeItem[] | null {
  const { themes } = options
  if (!themes || themes.length <= 1) {
    return null
  }
  return themes.map((t, index) => ({
    index,
    variantId: String(t.id),
    title: String(t.title ?? ""),
    imageSrc: getLiquidProductVariantImageSrc({ variant: t }),
  }))
}

/**
 * Suggestion chooser: Front tab only, parent `suggestedMessagingArea`, at least one visible face with TEXT,
 * and a group listing that area.
 */
export function getUvInsertSuggestionGroupsForTab(options: {
  config: UVInsertConfig
  tabIndex: number
  suggestedMessaging: SuggestedMessaging
}): UvInsertChooserSuggestionGroup[] | null {
  const { config, tabIndex, suggestedMessaging } = options
  if (!suggestedMessaging.length) {
    return null
  }
  if (
    !isUvInsertFrontRegionTab({
      faceElements: config.faceElements,
      tabIndex,
    })
  ) {
    return null
  }
  const declared = config.suggestedMessagingArea
  if (declared === undefined) {
    return null
  }
  const faces = getFaceElementsForRegionTab({
    faceElements: config.faceElements,
    tabIndex,
  })
  const hasText = faces.some((face) => face.elementConfig.some((el) => el.type === "TEXT"))
  if (!hasText) {
    return null
  }
  const matched: UvInsertChooserSuggestionGroup[] = []
  const seen = new Set<string>()
  suggestedMessaging.forEach((group, i) => {
    const areas = group.area ?? []
    if (!areas.includes(declared)) {
      return
    }
    const dedupeKey = `${group.name}\0${areas.join(",")}`
    if (seen.has(dedupeKey)) {
      return
    }
    seen.add(dedupeKey)
    matched.push({
      key: `sg-${i}`,
      name: group.name,
      suggestions: group.suggestions ?? [],
    })
  })
  return matched.length > 0 ? matched : null
}

export type UvInsertChooserPlan = {
  visible: boolean
  themeItems: UvInsertChooserThemeItem[] | null
  suggestionGroups: UvInsertChooserSuggestionGroup[] | null
}

/**
 * Theme chooser: multiple themes and Front tab only.
 * Suggestions: parent `suggestedMessagingArea`, Front tab with TEXT, matching suggestion groups.
 */
export function getUvInsertChooserPlan(options: {
  config: UVInsertConfig
  tabIndex: number
  suggestedMessaging: SuggestedMessaging
}): UvInsertChooserPlan {
  const { config, tabIndex, suggestedMessaging } = options
  const onFront = isUvInsertFrontRegionTab({
    faceElements: config.faceElements,
    tabIndex,
  })
  const themeItems =
    onFront && config.themes.length > 1
      ? getUvInsertThemeChooserItems({ themes: config.themes })
      : null
  const suggestionGroups = getUvInsertSuggestionGroupsForTab({
    config,
    tabIndex,
    suggestedMessaging,
  })
  return {
    visible: themeItems !== null || suggestionGroups !== null,
    themeItems,
    suggestionGroups,
  }
}
